import { useState } from 'react';

// Tap-based applied practice (docs/LESSON_FLOW.md § UI Primitives — PracticeCard).
// 4 options, single-select. Submit reveals correctness + rationale, then Continue
// advances. Used by StudyConsole (theme-aware CSS vars); the optional `dark`
// prop selects a hardcoded dark palette for any fixed-dark surface.
//
// props:
//   practice: { concept_id, question, options:[{letter,text}], correct, rationale }
//   onComplete: (result) => void   result = { selected_letter, correct, concept_id }
//   disabled: boolean
//   dark: boolean (hardcoded dark palette for fixed-dark surfaces)
export default function PracticeCard({ practice, onComplete, disabled = false, dark = false }) {
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  if (!practice?.options?.length) return null;

  const C = dark ? {
    card: '#21212b', opt: '#2a2a36', border: 'rgba(255,255,255,0.10)', text: '#e8e6e1',
    muted: '#6b6760', teal: '#2dd4bf', tealText: '#0f0f12',
    good: '#34d399', goodBg: 'rgba(52,211,153,0.12)', bad: '#f87171', badBg: 'rgba(248,113,113,0.12)',
    tealBg: 'rgba(45,212,191,0.10)', ration: 'rgba(255,255,255,0.04)',
  } : {
    card: 'var(--bg-elevated)', opt: 'var(--bg-card)', border: 'var(--border-medium)', text: 'var(--text-primary)',
    muted: 'var(--text-muted)', teal: 'var(--accent-primary)', tealText: 'var(--accent-text)',
    good: 'var(--progress-strong)', goodBg: 'rgba(52,211,153,0.12)', bad: 'var(--progress-weak)', badBg: 'rgba(248,113,113,0.12)',
    tealBg: 'var(--accent-soft)', ration: 'var(--bg-card)',
  };

  const isCorrect = (l) => l === practice.correct;
  const correct = selected != null && isCorrect(selected);

  const finish = () => {
    onComplete?.({ selected_letter: selected, correct, concept_id: practice.concept_id || null });
  };

  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '18px 20px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: C.teal, boxShadow: `0 0 8px ${C.teal}` }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>Practice</span>
      </div>

      <p style={{ color: C.text, fontSize: 15, lineHeight: 1.6, fontWeight: 500, margin: '0 0 14px' }}>{practice.question}</p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {practice.options.map((opt) => {
          const sel  = selected === opt.letter;
          const good = submitted && isCorrect(opt.letter);
          const bad  = submitted && sel && !isCorrect(opt.letter);
          const borderColor = good ? C.good : bad ? C.bad : sel ? C.teal : C.border;
          const bg = good ? C.goodBg : bad ? C.badBg : sel ? C.tealBg : C.opt;
          const badgeBg = good ? C.good : bad ? C.bad : sel ? C.teal : C.border;
          return (
            <button
              key={opt.letter}
              onClick={() => !submitted && !disabled && setSelected(opt.letter)}
              disabled={submitted || disabled}
              style={{
                display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
                padding: '13px 16px', minHeight: 44, boxSizing: 'border-box', borderRadius: 12,
                border: `1.5px solid ${borderColor}`, background: bg,
                color: good ? C.good : bad ? C.bad : C.text, fontSize: 14, fontWeight: 600,
                fontFamily: 'inherit', cursor: submitted ? 'default' : 'pointer', transition: 'all 0.2s',
              }}
            >
              <span style={{
                width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: badgeBg, color: C.tealText, fontSize: 11, fontWeight: 800,
              }}>
                {good ? '✓' : bad ? '✗' : opt.letter}
              </span>
              {opt.text}
            </button>
          );
        })}
      </div>

      {submitted && practice.rationale && (
        <div style={{ marginTop: 14, padding: '12px 14px', borderRadius: 10, background: C.ration, borderLeft: `2px solid ${correct ? C.good : C.teal}` }}>
          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: C.text }}>{practice.rationale}</p>
        </div>
      )}

      <button
        onClick={() => (submitted ? finish() : setSubmitted(true))}
        disabled={(!submitted && selected == null) || disabled}
        style={{
          width: '100%', marginTop: 16, padding: '13px 24px', minHeight: 44, borderRadius: 12, border: 'none',
          background: ((!submitted && selected == null) || disabled) ? C.opt : `linear-gradient(135deg, ${C.teal}, ${C.teal})`,
          color: ((!submitted && selected == null) || disabled) ? C.muted : C.tealText,
          fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
          cursor: ((!submitted && selected == null) || disabled) ? 'default' : 'pointer', letterSpacing: 0.2,
        }}
      >
        {submitted ? 'Continue →' : 'Submit'}
      </button>
    </div>
  );
}
